"use client";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import aa, { type InsightsMethodMap, type InsightsClient } from "search-insights";
import { useCookieConsent } from "~/hooks/cookiebot";
import { useUserToken } from "~/hooks/useUserToken";
import { useEnvVar } from "~/lib/config/context";
import { hashString } from "./helpers/hash-string";
import { readQueryID, saveQueryID } from "./helpers/insights";

export type InsightsState = {
	sendInsightsEvent: InsightsClient;
};

export const InsightsContext = createContext<InsightsState>({
	sendInsightsEvent: () => {},
});

let isInitialized = false;
const COOKIE_DURATION = 15552000; // 6 months

type InsightsArgs<M extends keyof InsightsMethodMap> = InsightsMethodMap[M][0];

export const InsightsProvider = ({ children }: { children: React.ReactNode }) => {
	const algoliaAppId = useEnvVar("NEXT_PUBLIC_ALGOLIA_APP_ID");
	const algoliaInsightsApiKey = useEnvVar("NEXT_PUBLIC_ALGOLIA_INSIGHTS_API_KEY");
	// For testing purposes, set consentToTracking to true, as there is no cookie banner on localhost
	const consentToTracking = useCookieConsent("statistics");
	const token = useUserToken();

	const initializeInsights = async () => {
		if (isInitialized) {
			return;
		}

		if (!algoliaAppId || !algoliaInsightsApiKey) {
			return;
		}

		const hashedToken = await hashString(token?.sub);

		aa("init", {
			appId: algoliaAppId,
			apiKey: algoliaInsightsApiKey,
			useCookie: consentToTracking,
			cookieDuration: consentToTracking ? COOKIE_DURATION : 0,
			userHasOptedOut: !consentToTracking,
			userToken: !token?.authenticated ? hashedToken : undefined,
			authenticatedUserToken: token?.authenticated ? hashedToken : undefined,
		});

		isInitialized = true;
	};

	const updateInsights = () => {
		aa("init", {
			partial: true,
			useCookie: consentToTracking,
			cookieDuration: consentToTracking ? COOKIE_DURATION : 0,
			userHasOptedOut: !consentToTracking,
		});
	};

	useEffect(() => {
		if (!isInitialized || !token?.sub) {
			return;
		}

		let cancelled = false;

		(async () => {
			const hashedToken = await hashString(token.sub);
			if (cancelled) return;

			if (token.authenticated) {
				aa("setAuthenticatedUserToken", hashedToken);
			} else {
				aa("setAuthenticatedUserToken", undefined);
				aa("setUserToken", hashedToken);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isInitialized, token]);

	useEffect(() => {
		if (isInitialized) {
			updateInsights();
		}
	}, [isInitialized, consentToTracking]);

	// Wrapper over the "aa" function to ensure that the insights client is initialized
	// before sending any events
	const sendInsightsEvent = useCallback(
		async <M extends keyof InsightsMethodMap>(method: M, ...args: InsightsArgs<M>) => {
			await initializeInsights();
			return (aa as InsightsClient)(method, ...args);
		},
		// ensure the sendInsightsEvent function always uses the latest values for token and consent (if they are updated)
		[token, consentToTracking],
	) as InsightsClient;

	const value = {
		sendInsightsEvent: sendInsightsEvent,
	};

	return <InsightsContext.Provider value={value}>{children}</InsightsContext.Provider>;
};

export function useInsights(initialQueryID?: string | null) {
	const ctx = useContext(InsightsContext);
	const [queryID, setQueryID] = useState<string | undefined>(() => readQueryID());

	useEffect(() => {
		if (initialQueryID && initialQueryID !== queryID) {
			saveQueryID(initialQueryID);
			setQueryID(initialQueryID);
		}
	}, [initialQueryID, queryID]);

	return { ...ctx, queryID };
}
